import React from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import { PropertyGroup } from '../../components';
import { richText } from '../../util/richText';
import { formatPriceWithCommas } from '../../util/formatPriceWithCommas';

import css from './ListingPage.module.css';
import config from '../../config';
import { convertMoneyToNumber } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';

const { Money } = sdkTypes;

const MIN_LENGTH_FOR_LONG_WORDS_IN_DESCRIPTION = 20;

/**
 * Format the given money to a string
 *
 * @param {Object} intl
 * @param {Money} value
 *
 * @return {String} formatted money value
 */
export const formatMoneyWithCents = (intl, value) => {
  if (!(value instanceof Money)) {
    throw new Error('Value must be a Money type');
  }
  const valueAsNumber = convertMoneyToNumber(value);

  // See: https://github.com/yahoo/react-intl/wiki/API#formatnumber
  const numberFormatOptions = {
    style: 'currency',
    currency: value.currency,
    currencyDisplay: 'symbol',
    useGrouping: true,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  };

  const finalValue = intl.formatNumber(valueAsNumber, numberFormatOptions);
  const convertedValueMaybe = finalValue.replaceAll('NZ', '');
  return convertedValueMaybe;
};

const SectionDetailsMaybe = props => {
  const { className, intl, publicData, customConfig, description, authorDisplayName } = props;
  const { listing, filters } = customConfig || {};

  if (!publicData || !customConfig || !listing?.enumFieldDetails) {
    return null;
  }

  const pickExtendedData = filterConfig => (rows, key) => {
    const publicDataValue = publicData[key];
    if (publicDataValue) {
      const filterIfItExists = filterConfig.find(f => f.id === key);
      const filterOptions = filterIfItExists?.config?.options || [];
      const value = filterOptions.find(o => o.key === publicDataValue)?.label || publicDataValue;
      const label = filterIfItExists?.label || `${key.charAt(0).toUpperCase()}${key.slice(1)}`;

      return rows.concat({ key, value, label });
    }
    return rows;
  };

  const {
    depth,
    height,
    width,
    colour,
    countryOfOrigin,
    age,
    rrpPrice,
    shippingEnabled,
    pickupEnabled,
    shippingPriceInSubunitsOneItem,
    location,
  } = publicData || {};
  const existingExtendedData = listing?.enumFieldDetails.reduce(pickExtendedData(filters), []);

  const splittedAddress = location?.address ? location?.address.split(',').map(item => item.trim()) : [];
  let shortLocation = splittedAddress[splittedAddress.length - 2];
  if (splittedAddress.length > 2) {
    const suburb = splittedAddress[splittedAddress.length - 3];
    const cityAndZipString = splittedAddress[splittedAddress.length - 2];
    let city = cityAndZipString;
    let zipCode = '';

    if (cityAndZipString.includes(' ')) {
      const cityAndZip = cityAndZipString.split(' ').map(item => item.trim());
      city = cityAndZip[0];
      zipCode = `, ${cityAndZip[1]}`;
    }

    shortLocation = `${suburb}, ${city}${zipCode}`;
  }

  const detailsOptions = [
    {
      label: 'Colour:',
      value: colour,
    },
    {
      label: 'Dimensions:',
      value: depth && height && width && `${height} x ${width} x ${depth} (h x w x d)`,
    },
    {
      label: 'Country of origin:',
      value: countryOfOrigin,
    },
    {
      label: 'Age/era:',
      value: age,
    },
    {
      label: 'RRP:',
      // value: rrpPrice && `$${formatPriceWithCommas((rrpPrice / 100).toFixed(0))}`,
      value: intl && Number.isInteger( rrpPrice )
        ? formatMoneyWithCents( intl, new Money( rrpPrice, process.env.REACT_APP_SHARETRIBE_MARKETPLACE_CURRENCY ))
        : null,
    },
    {
      label: 'Collection:',
      value: (() => {
        if (shippingEnabled && shippingPriceInSubunitsOneItem && pickupEnabled) {
          return `Shipping: $${formatPriceWithCommas((shippingPriceInSubunitsOneItem / 100).toFixed(0))} / Pickup: ${shortLocation}`;
        } else if (shippingEnabled && !shippingPriceInSubunitsOneItem && pickupEnabled) {
          return `Shipping: Fee not provided / Pickup: ${shortLocation}`;
        } else if (shippingEnabled && !shippingPriceInSubunitsOneItem && !pickupEnabled) {
          return `Shipping: Fee not provided`;
        } else if (shippingEnabled && shippingPriceInSubunitsOneItem && !pickupEnabled) {
          return `Shipping: $${formatPriceWithCommas((shippingPriceInSubunitsOneItem / 100).toFixed(0))}`;
        } else if (!shippingEnabled && pickupEnabled) {
          return `Pickup: ${shortLocation}`;
        } else {
          return 'No info provided';
        }
      })(),
    },
    {
      label: 'Product details:',
      value: richText(description, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_DESCRIPTION,
        longWordClass: css.longWord,
      }),
    },
  ];

  return existingExtendedData ? (
    <div className={className || css.sectionDetails}>
      {/* <h2 className={css.detailsTitle}>
        <FormattedMessage id="ListingPage.detailsTitle" />
      </h2> */}
      <ul className={css.details}>
        <li className={css.authorDetailsRow} key="sellerName">
          <span className={css.detailLabel}>
            <FormattedMessage id="ListingPage.authorDisplayName" />
          </span>
          <span className={css.detailValue}>
            {authorDisplayName}
          </span>
        </li>

        {/* {existingExtendedData.map(detail => (
          <li key={detail.key} className={css.detailsRow}>
            <span className={css.detailLabel}>{detail.label}</span>
            <span>{detail.value}</span>
          </li>
        ))} */}
        {detailsOptions
          .filter(o => o.value)
          .map((o, index) => (
            <li className={css.detailsRow} key={index}>
              <span className={css.detailLabel}>{o.label}</span>
              <span className={css.detailValue}>{o.value}</span>
            </li>
          ))}
        {/* <li className={css.descriptionRow}>
          <span className={css.detailLabel}>
            Product details:
          </span>
          <span className={css.detailValue}>
            {richText(description, {
              longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_DESCRIPTION,
              longWordClass: css.longWord,
            })}
          </span>
        </li> */}
      </ul>
    </div>
  ) : null;
};

export default SectionDetailsMaybe;
